'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';

import { useDonate } from '../donate-context.tsx';
import { DonateErrorObject, DonateVariants } from '../donate.types.ts';
import { useDonateValidation } from '../hooks/use-donate-validation.tsx';

const DonateErrorAlert: React.FC<{ message: string | DonateErrorObject }> = ({
  message,
}) => {
  const { t } = useTranslation(['core']);

  if (typeof message === 'string') {
    return (
      <Alert key="validation-error" variant="negative" message={message} />
    );
  }

  const titleNode =
    message.title && t(message.title.key, message.title.payload);

  const bodyNode = message.message && (
    <IntlBlock
      i18nKey={message.message.key}
      values={{ ...message.message.payload }}
      components={{
        anchor: (
          <Link
            className="link"
            href={`mailto:${t('contact.emailGiftPlanning')}`}
          >
            {t('contact.emailGiftPlanning')}
          </Link>
        ),
      }}
    />
  );

  return (
    <Alert
      key="validation-error"
      variant="negative"
      title={titleNode}
      message={bodyNode}
    />
  );
};

export const DonateSubmitButton: React.FC<{
  className?: string;
  alt?: boolean;
}> = ({ className, alt }) => {
  const { t } = useTranslation(['donations']);
  const { state, variant } = useDonate();
  const { getCurrentAmount, validationState } = useDonateValidation();

  const donateAmount = getCurrentAmount() || '';
  const numericAmount = Number(donateAmount);

  const getButtonText = () => {
    if (state.otherSelected && numericAmount <= 0) {
      return t('donations:enterAnAmount');
    }
    if (alt) {
      return t('donations:donate');
    }
    if (variant === DonateVariants.grid && !state.otherSelected) {
      return t('donations:addToCart');
    }
    if (Number.isNaN(numericAmount)) {
      return t('donations:donate');
    }

    return t('donations:donateAmount', {
      context: state.donateFrequency,
      amount: numericAmount,
    });
  };

  return (
    <div className={`DonateSubmitButton grid gap-snug-xl ${className || ''}`}>
      <ButtonStyle
        variant="primary"
        size="lg"
        fullWidth
        disabled={validationState.isDisabled || state.isSubmitting}
        isBusy={state.isSubmitting}
      >
        <button
          type="submit"
          onClick={() => {
            trackClick(getButtonText());
          }}
        >
          {getButtonText()}
        </button>
      </ButtonStyle>

      {validationState.message && donateAmount && (
        <DonateErrorAlert message={validationState.message} />
      )}
    </div>
  );
};
